import { createClient } from '@supabase/supabase-js';
import { environment } from '../../../environments/environment';

const baseFetch: typeof fetch = (...args) => fetch(...args);

export const url = environment.supabase.url;
export const key = environment.supabase.anonKey;
export const PUBLIC_MEDIA_BUCKET = 'public-media';
export const RIDER_PRIVATE_BUCKET = 'rider-private';
export const PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS = 300;

export const getPublicUrl = (bucket: string, path: string): string =>
  `${url.replace(/\/$/, '')}/storage/v1/object/public/${bucket}/${path}`;

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: (callbackUrl) =>
      callbackUrl.pathname.replace(/\/+$/, '') === '/reset-password',
    debug: environment.debug,
  },

  global: {
    fetch: async (requestUrl, options = {}) => {
      try {
        const headers = new Headers(options.headers || {});

        if (environment.debug) {
          console.debug('[supabase] fetch', {
            url: String(requestUrl),
            method: (options as RequestInit).method ?? 'GET',
          });
        }

        return await baseFetch(requestUrl, {
          ...options,
          cache: 'no-store',
          headers,
        });
      } catch (error) {
        if (environment.debug) {
          console.error('[supabase] fetch failed', requestUrl, error);
        }

        throw error;
      }
    },
  },
});

export const getStorageObjectUrl = async (
  bucket: string,
  path: string,
): Promise<string> => {
  if (bucket !== RIDER_PRIVATE_BUCKET) {
    return getPublicUrl(bucket, path);
  }

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, PRIVATE_STORAGE_SIGNED_URL_TTL_SECONDS);

  if (error || !data?.signedUrl) {
    throw error ?? new Error('Could not create a private Storage URL.');
  }

  return data.signedUrl;
};
